import React from 'react';
import * as R from 'ramda';
import {
  ArrayField,
  Datagrid,
  DateField,
  Error,
  FunctionField,
  Loading,
  TextField,
  useQuery,
} from 'react-admin';
import { Box, Button, Typography } from '@material-ui/core';
import { green, grey } from '@material-ui/core/colors';
import { makeStyles } from '@material-ui/core/styles';
import { OpenInNew } from '@material-ui/icons';

import { NewProgressField, OfflineList } from 'resources/common';

const useTitleStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
  },
  title: {
    fontWeight: 'bold',
    marginBottom: '4px',
  },
  path: {
    fontSize: '0.75rem',
    color: '#666',
  },
});

const getCourseId = (record, course) => {
  const courseData = R.propOr({}, course, record);
  const openedxId = courseData.openedx || courseData.id || courseData.courseId;
  if (!openedxId) return course;

  return R.pipe(R.split(':'), R.drop(2), R.join(':'))(openedxId);
};

const getCompletionPercentage = (record) => {
  const total = R.pipe(R.propOr([], 'students'), R.length)(record);
  if (!total) return 0;
  return Math.round((R.propOr(0, 'completed', record) / total) * 100);
};

const TryHackMeLabProgressField = ({ record }) => {
  const total = R.pipe(R.propOr([], 'students'), R.length)(record);
  const completed = R.propOr(0, 'completed', record);
  return (
    <NewProgressField
      value={getCompletionPercentage(record)}
      text={`${completed}/${total}`}
    />
  );
};

const TryHackMeLabTitle = ({ record }) => {
  const classes = useTitleStyles();
  return (
    <div className={classes.container}>
      <div className={classes.title}>{record.title}</div>
      {record.submoduleTitle ? (
        <div className={classes.path}>{record.submoduleTitle}</div>
      ) : null}
    </div>
  );
};

TryHackMeLabTitle.defaultProps = {
  label: 'Lab',
};

TryHackMeLabProgressField.defaultProps = {
  label: 'Progress',
};

const StudentNameField = ({ record }) => (
  <span>{`${R.propOr('', 'firstName', record)} ${R.propOr(
    '',
    'lastName',
    record,
  )}`}</span>
);

StudentNameField.defaultProps = {
  label: 'Full Name',
};

const StudentProgressField = ({ record }) => {
  const completed = R.propOr(0, 'tasksCompleted', record);
  const total = R.propOr(0, 'tasksTotal', record);
  if (!total) return <span>Pending</span>;
  return <span>{`${completed}/${total}`}</span>;
};

StudentProgressField.defaultProps = {
  label: 'Tasks',
};

const TryHackMeRoomLink = ({ record }) => {
  if (!record.url) return null;
  return (
    <Button size="small" color="primary" target="_blank" href={record.url}>
      <OpenInNew style={{ paddingRight: '0.2em' }} />
      Open Room
    </Button>
  );
};

TryHackMeRoomLink.defaultProps = {
  label: 'Room',
};

const studentRowStyle = (record) => ({
  borderLeftColor: record.status === 'completed' ? green[500] : grey[500],
  borderLeftWidth: 5,
  borderLeftStyle: 'solid',
});

const TryHackMeStudentsList = ({ record }) => (
  <OfflineList
    list={R.propOr([], 'students', record)}
    uniqueKey="studentId"
    defaultSort={{ field: 'status', order: 'ASC' }}
    sorters={{ status: R.propOr('', 'status') }}
  >
    <Datagrid rowStyle={studentRowStyle}>
      <StudentNameField sortable={false} />
      <TextField source="email" sortable />
      <FunctionField
        label="Status"
        source="status"
        render={(record) => R.propOr('', 'status', record)}
        sortable
      />
      <StudentProgressField sortable={false} />
      <TextField label="Score" source="score" sortable />
      <DateField label="Completed At" source="completedAt" showTime />
    </Datagrid>
  </OfflineList>
);

const ModuleTryHackMeLabsTable = ({ record }) => {
  const labs = R.pipe(
    R.propOr([], 'submodules'),
    R.chain((submodule) =>
      R.pipe(
        R.propOr([], 'labs'),
        R.map((lab) => ({
          ...lab,
          submoduleTitle: submodule.title,
        })),
      )(submodule),
    ),
  )(record);

  if (R.isEmpty(labs)) return null;

  return (
    <OfflineList
      list={labs}
      uniqueKey="id"
      defaultSort={{ field: 'submoduleTitle', order: 'ASC' }}
      sorters={{ progress: getCompletionPercentage }}
    >
      <Datagrid rowClick="expand" expand={<TryHackMeStudentsList />}>
        <TryHackMeLabTitle />
        <FunctionField
          label="Required"
          source="required"
          render={(record) => (record.required ? 'Yes' : 'No')}
        />
        <TryHackMeLabProgressField source="progress" />
        <TryHackMeRoomLink sortable={false} />
      </Datagrid>
    </OfflineList>
  );
};

const TryHackMeLabsList = ({ record, course }) => {
  const courseId = getCourseId(record, course);
  const { data, loading, error } = useQuery({
    type: 'getOne',
    resource: 'tryhackme_labs',
    payload: {
      id: record.id,
      courseId,
    },
  });

  if (loading)
    return (
      <Box p="16px">
        <Loading />
      </Box>
    );

  if (error)
    if (error.status === 404) {
      return (
        <Box p="16px">
          <Typography variant="body2">
            Could not find THM labs for this course.
          </Typography>
        </Box>
      );
    }

  if (error)
    return (
      <Box p="16px">
        <Error error={error} errorInfo={error} />
      </Box>
    );

  const modules = R.propOr([], 'modules', data);
  if (R.isEmpty(modules)) {
    return (
      <Box p="16px">
        <Typography variant="body2">
          No TryHackMe labs found for this cohort.
        </Typography>
      </Box>
    );
  }

  return (
    <ArrayField source="modules" record={{ modules }}>
      <Datagrid
        rowClick="expand"
        expand={<ModuleTryHackMeLabsTable course={course} />}
      >
        <TextField label="Module" source="title" sortable={false} />
      </Datagrid>
    </ArrayField>
  );
};

function ByTryHackMeSubtab(props) {
  const { record, course } = props;
  return <TryHackMeLabsList record={record} course={course} />;
}

export default ByTryHackMeSubtab;
